import { useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Search,
  Users,
  Download,
  Eye,
  Phone,
  Mail,
  Calendar,
  StickyNote,
  Building2,
} from "lucide-react";
import {
  useContactos,
  useContacto,
  useCreateContacto,
  useUpdateContacto,
  useDeleteContacto,
  useImportarClientes,
  useAddActividad,
} from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";
import type { Contacto, ContactoEstado, ContactoFormData } from "../types";
import { Pagination, pageSlice } from "@modules/billing/components/Pagination";

const ESTADOS: { v: ContactoEstado; label: string; cls: string }[] = [
  { v: "nuevo", label: "Nuevo", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  { v: "contactado", label: "Contactado", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  { v: "cualificado", label: "Cualificado", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  { v: "cliente", label: "Cliente", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { v: "descartado", label: "Descartado", cls: "bg-rose-50 text-rose-700 border-rose-200" },
];

const ACT_ICON: Record<string, typeof Phone> = {
  llamada: Phone,
  email: Mail,
  reunion: Calendar,
  nota: StickyNote,
  tarea: StickyNote,
};

function estadoBadge(e: ContactoEstado) {
  return ESTADOS.find((x) => x.v === e) ?? ESTADOS[0];
}

const EMPTY: ContactoFormData = {
  nombre: "",
  empresa: "",
  email: "",
  telefono: "",
  cargo: "",
  fuente: "",
  estado: "nuevo",
  notas: "",
};

export function ContactosPage() {
  const { emisorId } = useMarketingStore();
  const [estado, setEstado] = useState<string>("");
  const [q, setQ] = useState("");
  const { data, isLoading } = useContactos(emisorId, {
    estado: estado || undefined,
    q: q || undefined,
  });
  const contactos = data?.data;

  const createMut = useCreateContacto();
  const updateMut = useUpdateContacto();
  const deleteMut = useDeleteContacto();
  const importMut = useImportarClientes();

  const [editing, setEditing] = useState<Contacto | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ContactoFormData>(EMPTY);
  const [detalleId, setDetalleId] = useState<number | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const paged = useMemo(() => pageSlice(contactos, page, pageSize), [contactos, page, pageSize]);

  function openCreate() {
    setForm(EMPTY);
    setCreating(true);
    setEditing(null);
  }
  function openEdit(c: Contacto) {
    setForm({
      nombre: c.nombre,
      empresa: c.empresa ?? "",
      email: c.email ?? "",
      telefono: c.telefono ?? "",
      cargo: c.cargo ?? "",
      fuente: c.fuente ?? "",
      estado: c.estado,
      notas: c.notas ?? "",
    });
    setEditing(c);
    setCreating(false);
  }
  function close() {
    setEditing(null);
    setCreating(false);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, payload: form });
    } else {
      await createMut.mutateAsync({ emisorId, payload: form });
    }
    close();
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <MarketingHeader
        title="Marketing"
        subtitle="Base de contactos y leads."
        right={
          <div className="flex gap-2 self-end">
            <button
              onClick={() => emisorId && importMut.mutate(emisorId)}
              disabled={!emisorId || importMut.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 font-bold text-sm transition"
            >
              {importMut.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              Importar clientes
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft transition"
            >
              <Plus size={16} /> Nuevo contacto
            </button>
          </div>
        }
      />

      {importMut.data && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 text-sm font-bold shrink-0">
          Importados {importMut.data.importados} · ya existían {importMut.data.omitidos}
        </div>
      )}

      <div className="flex flex-wrap gap-3 shrink-0">
        <select
          value={estado}
          onChange={(e) => {
            setEstado(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">Todos los estados</option>
          {ESTADOS.map((e) => (
            <option key={e.v} value={e.v}>
              {e.label}
            </option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar nombre, empresa o email…"
            className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm"
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {contactos && (
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
          <div className="overflow-auto max-h-[calc(100vh-340px)]">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Contacto</th>
                  <th className="text-left px-4 py-3 font-bold">Email</th>
                  <th className="text-left px-4 py-3 font-bold">Fuente</th>
                  <th className="text-center px-4 py-3 font-bold w-28">Estado</th>
                  <th className="text-center px-4 py-3 font-bold w-24">Score</th>
                  <th className="text-right px-4 py-3 font-bold w-32">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {contactos.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      No hay contactos.
                    </td>
                  </tr>
                )}
                {paged.map((c) => {
                  const badge = estadoBadge(c.estado);
                  return (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-4 py-2.5">
                        <div className="font-bold text-slate-900">{c.nombre}</div>
                        {c.empresa && c.empresa !== c.nombre && (
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <Building2 size={11} /> {c.empresa}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-slate-600">{c.email ?? "—"}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-500">{c.fuente ?? "—"}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold border ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-amber-400 to-emerald-500"
                              style={{ width: `${c.lead_score}%` }}
                            />
                          </div>
                          <span className="text-xs font-mono font-bold text-slate-600 w-7 text-right">
                            {c.lead_score}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setDetalleId(c.id)}
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                            title="Ver ficha"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            onClick={() => openEdit(c)}
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                            title="Editar"
                          >
                            <Edit3 size={15} />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Eliminar a ${c.nombre}?`)) deleteMut.mutate(c.id);
                            }}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-700"
                            title="Eliminar"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={contactos.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      )}

      {detalleId && (
        <ContactoDetalle
          emisorId={emisorId!}
          contactoId={detalleId}
          onClose={() => setDetalleId(null)}
        />
      )}

      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">
                {editing ? "Editar contacto" : "Nuevo contacto"}
              </h2>
              <button type="button" onClick={close} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre *</label>
                <input
                  required
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Empresa</label>
                <input
                  type="text"
                  value={form.empresa ?? ""}
                  onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cargo</label>
                <input
                  type="text"
                  value={form.cargo ?? ""}
                  onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Email</label>
                <input
                  type="email"
                  value={form.email ?? ""}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Teléfono</label>
                <input
                  type="text"
                  value={form.telefono ?? ""}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Fuente</label>
                <input
                  type="text"
                  value={form.fuente ?? ""}
                  onChange={(e) => setForm({ ...form, fuente: e.target.value })}
                  placeholder="web, evento, referido…"
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Estado</label>
                <select
                  value={form.estado}
                  onChange={(e) => setForm({ ...form, estado: e.target.value as ContactoEstado })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  {ESTADOS.map((e) => (
                    <option key={e.v} value={e.v}>
                      {e.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Notas</label>
                <textarea
                  rows={3}
                  value={form.notas ?? ""}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMut.isPending || updateMut.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
              >
                <Save size={14} /> Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ─── Ficha de contacto con timeline ───
function ContactoDetalle({
  emisorId,
  contactoId,
  onClose,
}: {
  emisorId: number;
  contactoId: number;
  onClose: () => void;
}) {
  const { data: c, isLoading } = useContacto(emisorId, contactoId);
  const addAct = useAddActividad();
  const [tipo, setTipo] = useState("nota");
  const [asunto, setAsunto] = useState("");

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!asunto.trim()) return;
    await addAct.mutateAsync({ emisorId, contactoId, payload: { tipo, asunto } });
    setAsunto("");
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-auto">
        {isLoading || !c ? (
          <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
            <Loader2 className="animate-spin" size={18} /> Cargando…
          </div>
        ) : (
          <>
            <header className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-black text-slate-900">{c.nombre}</h2>
                <p className="text-sm text-slate-500">
                  {c.empresa ?? ""} {c.cargo ? `· ${c.cargo}` : ""}
                </p>
                <div className="flex gap-2 mt-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold border ${estadoBadge(c.estado).cls}`}>
                    {estadoBadge(c.estado).label}
                  </span>
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">
                    Score {c.lead_score}
                  </span>
                </div>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>

            <div className="grid grid-cols-2 gap-3 text-sm mb-5">
              {c.email && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail size={14} className="text-slate-400" /> {c.email}
                </div>
              )}
              {c.telefono && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone size={14} className="text-slate-400" /> {c.telefono}
                </div>
              )}
              {c.fuente && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Users size={14} className="text-slate-400" /> {c.fuente}
                </div>
              )}
            </div>

            {/* Oportunidades */}
            {c.oportunidades && c.oportunidades.length > 0 && (
              <div className="mb-5">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">
                  Oportunidades
                </h3>
                <div className="space-y-1">
                  {c.oportunidades.map((o) => (
                    <div
                      key={o.id}
                      className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                    >
                      <span className="font-bold text-slate-800">{o.titulo}</span>
                      <span className="font-mono text-slate-600">
                        {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(o.valor)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Añadir actividad */}
            <form onSubmit={add} className="flex gap-2 mb-4">
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                className="rounded-lg border border-slate-300 px-2 py-2 text-sm"
              >
                <option value="nota">Nota</option>
                <option value="llamada">Llamada</option>
                <option value="email">Email</option>
                <option value="reunion">Reunión</option>
                <option value="tarea">Tarea</option>
              </select>
              <input
                type="text"
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                placeholder="Registrar actividad…"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="submit"
                disabled={addAct.isPending}
                className="rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-3 py-2 text-sm font-bold disabled:opacity-50"
              >
                <Plus size={16} />
              </button>
            </form>

            {/* Timeline */}
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">
              Actividad
            </h3>
            {!c.actividades || c.actividades.length === 0 ? (
              <p className="text-sm text-slate-400">Sin actividades registradas.</p>
            ) : (
              <div className="space-y-2">
                {c.actividades.map((a) => {
                  const Icon = ACT_ICON[a.tipo] ?? StickyNote;
                  return (
                    <div key={a.id} className="flex gap-3">
                      <div className="grid place-items-center w-8 h-8 rounded-full bg-slate-100 text-slate-500 shrink-0">
                        <Icon size={14} />
                      </div>
                      <div className="flex-1 border-b border-slate-100 pb-2">
                        <div className="font-bold text-sm text-slate-800">{a.asunto}</div>
                        {a.descripcion && (
                          <div className="text-xs text-slate-500">{a.descripcion}</div>
                        )}
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {a.fecha && new Date(a.fecha).toLocaleString("es-ES")}
                          {a.usuario_nombre && ` · ${a.usuario_nombre}`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
